import { Pill, Text } from "@clipboard-health/ui-components";
import { Box, Stack, useTheme } from "@mui/material";
import { GroupedFiniteList } from "@src/appV2/redesign/components/GroupedFiniteList";
import { TimeSlotIcon } from "@src/appV2/redesign/Shift/TimeSlotIcon";
import { type WorkerEventForRating } from "@src/appV2/redesign/Tiers/api/useWorkerEventsForRating";
import { format, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

interface WorkerEventsListProps {
  workerEvents: WorkerEventForRating[];
  workerTimeZone: string;
}

export function WorkerEventsList(props: WorkerEventsListProps) {
  const { workerEvents, workerTimeZone } = props;
  const theme = useTheme();

  const workerEventsWithKey = workerEvents.map((workerEvent) => ({
    ...workerEvent,
    key: workerEvent.id,
  }));

  return (
    <Stack spacing={4} sx={{ height: "40vh" }}>
      <Text semibold variant="h4">
        Rating Breakdown
      </Text>
      {workerEvents.length > 0 ? (
        <GroupedFiniteList
          sx={{ paddingBottom: 8 }}
          items={workerEventsWithKey}
          groupBy={(workerEvent: WorkerEventForRating) => {
            return workerEvent.eventDate.toISOString();
          }}
          sortGroupKeys={(keyA: string, keyB: string) => {
            // Reverse the regular sort so events are descending chronological order
            return keyB.localeCompare(keyA);
          }}
          renderGroupHeader={(eventDateIso: string) => {
            const eventDate = utcToZonedTime(parseISO(eventDateIso), workerTimeZone);
            return (
              <Text semibold variant="body1" sx={{ color: theme.palette.text.tertiary }}>
                {format(eventDate, "EEE, MMM d")}
              </Text>
            );
          }}
          renderItem={(workerEvent: WorkerEventForRating) => (
            <Stack spacing={0}>
              <Stack direction="row" alignItems="top" gap={5}>
                <Stack
                  direction="row"
                  alignItems="top"
                  gap={3}
                  sx={{ minWidth: 0, flex: 1, maxWidth: "100%" }}
                >
                  <TimeSlotIcon
                    timeSlot={workerEvent.shiftTimeSlot}
                    variant="filled"
                    size="medium"
                    sx={{
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.secondary,
                      padding: 1,
                    }}
                  />
                  <Stack
                    direction="column"
                    spacing={2}
                    sx={{ minWidth: 0, flex: 1, paddingTop: 1 }}
                  >
                    <Text
                      semibold
                      noWrap
                      variant="body1"
                      sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {workerEvent.shiftDescription}
                    </Text>
                    <Text
                      noWrap
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {workerEvent.facilityName}
                    </Text>
                  </Stack>
                </Stack>
                <Pill
                  label={workerEvent.eventDescription}
                  size="medium"
                  sx={{ minWidth: "fit-content" }}
                  color={(theme) =>
                    workerEvent.isSuccess
                      ? theme.palette.intent?.success.text
                      : theme.palette.intent?.error.text
                  }
                  backgroundColor={(theme) =>
                    workerEvent.isSuccess
                      ? theme.palette.intent?.success.background
                      : theme.palette.intent?.error.background
                  }
                />
              </Stack>
            </Stack>
          )}
        />
      ) : (
        <Box>
          <Text variant="body1" color="text.secondary">
            Starting fresh!
          </Text>
        </Box>
      )}
    </Stack>
  );
}
